import { PanelFormCreateComponent } from '../components/creation/panel-form-create/panel-form-create.component';
import { FormsMainHandoffService } from '../services/forms-main-handoff.service';
import type { NativeForm } from '../services/forms-api.service';
import type { PopoverDismissGuard } from '@frontendlib/components/features/popover/types/config';
import { ChangeDetectionStrategy, Component, inject, Input, output, signal, viewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { CLOSE_POPOVER } from '@frontendlib/components/features/popover/types/close-popover.injection-token';
import { ContextMenuDividerComponent } from '@frontendlib/components/features/popover/components/context-menu-divider/context-menu-divider.component';
import { ContextMenuItemComponent } from '@frontendlib/components/features/popover/components/context-menu-item/context-menu-item.component';
import { PostponeContextMenuComponent } from '@modules/common/components/postpone-context-menu/postpone-context-menu.component';
import { injectFollowUpWording } from '@modules/common/utils/follow-up-wording';
import { SimpleInputComponent } from '@modules/form-controls/components/simple-input/simple-input.component';
import { AddKnotsComponent } from '@modules/knots/components/add-knots/add-knots.component';
import { AddTagsComponent } from '@modules/tags/components/add-tags/add-tags.component';
import { AddConnectionsComponent } from '@modules/connections/components/add-connections/add-connections.component';
import { AccessLimitGateService } from '@modules/settings/services/access-limit-gate.service';
import { Injector } from '@angular/core';
import type { FormFolderSummaryDto } from '@sharedlib/dtos/dto/forms-app/form.dto';
import { firstValueFrom } from 'rxjs';
import { FormFoldersStitchService } from '../services/form-folders.service';
import { FormsWorkspaceStore } from '../services/forms-workspace.store';
import { FormsSidebarActionsService } from '../services/forms-sidebar-actions.service';
import { injectFullWindowStart } from '@modules/common/services/full-window.service';
import { Application } from '@modules/common/types/application';
import { titleProblem } from '../components/creation/forms-quick-form/forms-quick-form.component';
import { APPLICATION_SCOPE_TOKEN, ApplicationScope } from '@modules/common/types/ApplicationScope';

/** Boards folder menu markup, delegating to the existing Forms folder CAS/retry authority. */
@Component({
  selector: 'app-form-folder-context-menu', standalone: true, changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './form-folder-context-menu.component.html',
  styleUrls: ['../../elements/components/context-menus/stitch-context-menu/stitch-context-menu.component.less'],
  imports: [Button2Component, ContextMenuItemComponent, ContextMenuDividerComponent, SimpleInputComponent,
    PostponeContextMenuComponent, AddKnotsComponent, AddTagsComponent, AddConnectionsComponent, PanelFormCreateComponent],
})
export class FormFolderContextMenuComponent {
  @Input() public set item(value: FormFolderSummaryDto) { this.folder = value; this.renameControl.setValue(value.title); }
  protected folder: FormFolderSummaryDto | undefined;
  public readonly open = output<boolean>();
  private readonly scope = inject(APPLICATION_SCOPE_TOKEN, { optional: true });
  private readonly creationControl = viewChild(PanelFormCreateComponent);
  private readonly handoff = inject(FormsMainHandoffService);
  protected readonly creationDismissGuard: PopoverDismissGuard = { canDismiss: () => this.creationControl()?.canLeaveHost() ?? true, setOpen: () => undefined };
  protected readonly beforeCreate = async (initiator?: PanelFormCreateComponent): Promise<boolean> => {
    const folder = this.folder;
    return !!folder && !folder.deleted && folder.accountId === this.store.space.contextId() && await this.store.beforeLeave(false, initiator?.activePreflightGuardKey);
  };
  protected async openCreated(item: NativeForm): Promise<void> {
    if (item.accountId !== this.store.space.contextId()) return;
    await this.store.refresh();
    if (this.scope === ApplicationScope.DYNAMIC_PANEL) await this.handoff.handOff({ kind: 'form', id: item.id, intent: 'editor', spaceId: item.accountId, spaceType: this.store.space.type() });
    else if (await this.store.open(item, true)) this.startFullWindow?.();
    this.close();
  }
  protected readonly store = inject(FormsWorkspaceStore);
  private readonly folders = inject(FormFoldersStitchService);
  private readonly actions = inject(FormsSidebarActionsService);
  private readonly injector = inject(Injector);
  private readonly closePopover = inject(CLOSE_POPOVER, { optional: true });
  private readonly startFullWindow = injectFullWindowStart(Application.forms);
  protected readonly followUpWording = injectFollowUpWording();
  protected readonly renameControl = new FormControl('', { nonNullable: true });
  protected readonly renameError = signal<string | null>(null);
  protected summaries(): FormFolderSummaryDto[] { return this.folder ? [this.folder] : []; }
  protected close(): void { this.closePopover?.next(); }
  protected handleOpen(): void { this.open.emit(false); }
  protected selectAll(): void { this.open.emit(true); }
  private async select(): Promise<boolean> {
    const folder = this.folder; if (!folder || this.store.busy() || !(await this.store.beforeLeave())) return false;
    await this.store.openFolder(folder, this.store.foldersOpen());
    return this.store.selectedFolder()?.id === folder.id && this.store.selectedFolder()?.accountId === folder.accountId;
  }
  protected setState(changes: Record<string, unknown>): void {
    const folder = this.folder;
    if (!folder) return;
    if (changes['archived'] === true || changes['deleted'] === true) this.actions.changeFolderState(folder, changes['deleted'] ? 'trash' : 'archive', confirmed => this.applyState(confirmed));
    else this.applyState(changes);
  }
  private applyState(changes: Record<string, unknown>): void {
    const folder = this.folder;
    if (folder && ('deleted' in changes || 'archived' in changes || 'title' in changes) && this.store.selectedFolder()?.id !== folder.id)
      void this.apply(async () => { await firstValueFrom(this.folders.bunchUpdate({ ids: [folder.id] }, changes, { tenantItems: [folder] }), { defaultValue: undefined }); await this.store.refresh(); });
    else void this.apply(async () => { if (await this.select()) await this.store.folderMetadata(changes); });
  }
  protected pin(): void { this.setState({ pinned: !this.folder?.pinned }); }
  protected flag(): void { this.setState({ flagged: !this.folder?.flagged }); }
  protected rename(): void {
    const title = this.renameControl.value.trim(), problem = titleProblem(title); this.renameError.set(problem);
    if (!problem) { this.setState({ title }); this.close(); }
  }
  protected duplicate(): void {
    if (this.injector.get(AccessLimitGateService).blocks('createItem', this.injector)) return;
    void this.apply(async () => { if (await this.select()) await this.store.duplicateFolder(); });
  }
  protected delete(): void {
    const folder = this.folder; if (!folder) return;
    void this.apply(async () => {
      if (!(await this.store.beforeLeave())) return;
      const deleted = await firstValueFrom(this.folders.deletePermanently({ ids: [folder.id] }, { tenantItems: [folder] }), { defaultValue: false });
      if (deleted && this.store.selectedFolder()?.id === folder.id) await this.store.close();
      await this.store.refresh();
    });
  }
  private async apply(operation: () => Promise<void>): Promise<void> {
    try { await operation(); } catch (error) { this.store.fail(error, 'Couldn’t update the folder. It is back as it was.'); }
  }
}
